import { useMemo, useSyncExternalStore } from "react";
import {
  getGameViewArtWidth,
  subscribeGameViewArtWidth,
} from "../utils/gameViewArtWidth";
import { useSelector } from "react-redux";
import { TextStyle, TextMetrics } from "pixi.js";
import { useDimensions } from "../Contexts/DimensionsContext";
import { USER_INTERFACE_BUTTON_CONFIG } from "../pixiSettings";
import { PixiSettings } from "../pixiSettings";
import { FONT_CONFIG } from "../pixiSettings";
import { formatToCustomLocale } from "../utilities";
import { FEATURES_CONFIG } from "../featuresConfig";
import { selectCurrency, selectBets } from "../State/selectors";
import { holdAndWinSceneScale } from "../Components/Animations/HoldAndWinGameView/HoldAndWinGameViewController";

// Toolbar layout constraints
const MAX_TOOLBAR_WIDTH = 1920;

// ── Desktop: never wider than the Hold & Win room's background ──
//
// (3 Orbs of Zeus 744b309b, 2026-10-08.) The bar takes the screen's width
// unless the Hold & Win room is narrower — then it stops inside that room's
// background, in every room (Vladimir, 2026-10-09: always the Hold & Win
// size). hold_and_win_game_view `hnw_bg` spans -1300..+1300 spine units,
// drawn at holdAndWinSceneScale. FILL keeps the end buttons off the art's
// darkened edge.
const HOLD_AND_WIN_BACKGROUND_UNITS = 2600;
const SCENE_BACKGROUND_FILL = 0.94;
// Ultra-wide: the bar reaches past that room's art (2026-10-22: "too joined
// in" — its room is narrow there, and smaller again with its reel padding).
// const SCENE_BACKGROUND_FILL_BY_LAYOUT = { ULTRA_WIDE_DESKTOP: 1.2 };
// Reverted (2026-10-22: "UI must not exceed game view's width") — with the
// background capped at its art, 0.94 already spans the game view.
const SCENE_BACKGROUND_FILL_BY_LAYOUT = {};
// ...and never wider than the BASE game view's art on screen (2026-10-22:
// "must be within and inside bounds") — this share of it, the end buttons
// inside its edge.
// const GAME_VIEW_ART_FILL = 0.9;
// Out to the game view's edges, a small gap kept (2026-10-22: "spread the ui
// to the edges of the gameview … small padding to not touch edge").
// const GAME_VIEW_ART_FILL = 0.96;
// const GAME_VIEW_ART_FILL = 0.98; // a bit more spread (2026-10-22)
const GAME_VIEW_ART_FILL = 0.99; // a tiny bit more (2026-10-22)
const MAX_TOOLBAR_WIDTH_MOBILE_LANDSCAPE_MULTIPLIER = 10;

// Max bet button configuration - matches ButtonMaxBet component (moved outside to prevent recreation)
const MAX_BET_BUTTON_CONFIG = {
  BASE_WIDTH: 80,
  BASE_HEIGHT: 80,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.97,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.75,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.3,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.1,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0,
    },
  },
};

const MAX_BET_BUTTON_CONFIG_DESKTOP = {
  BASE_WIDTH: 93,
  BASE_HEIGHT: 64,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 0.9,
    },
  },
};

// Per-tier height multipliers (applied to buttonConfig.height)
const TOOLBAR_HEIGHT_MULTIPLIERS = {
  MOBILE_PORTRAIT: 0,
  MOBILE_LANDSCAPE: 1.35,
  // MOBILE_SMALL_LANDSCAPE: 1.5,
  // As MOBILE_LANDSCAPE (2026-10-16): crossing 736 px wide made the bar ~11%
  // taller with nothing else changed, and it ran up towards the reels.
  MOBILE_SMALL_LANDSCAPE: 1.35,
  TABLET_PORTRAIT: 0,
  TABLET_LANDSCAPE: 1.85,
  DESKTOP: 2.18,
};

// Visual height multipliers for portrait toolbar background (doesn't affect layout)
const TOOLBAR_VISUAL_HEIGHT_MULTIPLIERS = {
  MOBILE_SMALL_PORTRAIT: 2.2,
  MOBILE_PORTRAIT: 1.575,
  // TABLET_PORTRAIT: 2.0,
  // An iPad upright (2026-10-29, layout TABLET_PORTRAIT on the MOBILE device):
  // MOBILE_PORTRAIT's — the small phone's 2.2 ran the bar too high.
  // TABLET_PORTRAIT: 1.575,
  // TABLET_PORTRAIT: 2.17, // a couple of notches under 2.2 (2026-10-29)
  // TABLET_PORTRAIT: 2.16,
  // TABLET_PORTRAIT: 2.15,
  TABLET_PORTRAIT: 2.05,
};

/**
 * Hook for calculating Toolbar dimensions with width capping
 * - Mobile landscape: capped dynamically at uiSymbolSize * MAX_TOOLBAR_WIDTH_MOBILE_LANDSCAPE_MULTIPLIER
 * - All other cases: capped at MAX_TOOLBAR_WIDTH (1920px)
 * Simpler alternative to useToolbarDimensions for the bottom toolbar
 * Returns toolbar object with width, height, and layout positions based on device/orientation
 */
export const useSecondaryToolbar = () => {
  const {
    dimensions,
    device,
    orientation,
    getResponsiveConfig,
    uiSymbolSize,
    uiSymbolScale,
    isMobileSmall,
    layout,
  } = useDimensions();
  const currency = useSelector(selectCurrency);
  const bets = useSelector(selectBets);

  // Memoize font multiplier calculation (used by both balance and bet width calculations)
  const fontMultiplier = useMemo(() => {
    if (device === PixiSettings.DEVICE.MOBILE) {
      return orientation === PixiSettings.ORIENTATION.PORTRAIT ? 1.0 : 1.1;
    } else if (device === PixiSettings.DEVICE.TABLET) {
      return orientation === PixiSettings.ORIENTATION.PORTRAIT ? 1.65 : 1.25;
    } else {
      return 1.0; // Desktop
    }
  }, [device, orientation]);

  // Memoize button config
  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // The base game view's art width on screen (GameViewSimpleController).
  const gameViewArtWidth = useSyncExternalStore(
    subscribeGameViewArtWidth,
    getGameViewArtWidth,
  );

  // Memoize max width calculation
  const maxWidth = useMemo(() => {
    if (
      device === PixiSettings.DEVICE.MOBILE &&
      orientation === PixiSettings.ORIENTATION.LANDSCAPE
    ) {
      // return uiSymbolSize * MAX_TOOLBAR_WIDTH_MOBILE_LANDSCAPE_MULTIPLIER;
      // ── Inset from the screen edges (FEATURES_CONFIG.LAYOUT.NO_TABLET) ──
      // the right column sits partly past the toolbar's edge. Fine while the
      // cap keeps the toolbar narrower than the screen; a wider landscape
      // screen (e.g. 1000x520, a tablet before) hit the screen width and the
      // column ran off the right side. Keep 0.4 of a button in from each edge
      // then (centred, so both sides move in). Small phones place their
      // column inside already — unchanged.
      const cap = uiSymbolSize * MAX_TOOLBAR_WIDTH_MOBILE_LANDSCAPE_MULTIPLIER;
      if (!FEATURES_CONFIG.LAYOUT.NO_TABLET || isMobileSmall) return cap;
      return Math.min(cap, dimensions.width - buttonConfig.width * 0.4 * 2);
    }
    // Desktop: full screen width, and deliberately nothing to do with the
    // game scene.
    //
    // This used to be gameWidth x 1.2, where gameWidth is the reel area's
    // fitted width — so it moved with SYMBOL_WIDTH, with the row count, and
    // with anything done to the board. Going to 5x6 dropped the fit scale from
    // 1.0 to 0.97 and the bar lost ~240px. In Zeus that same expression always
    // exceeded the screen and clamped to its width, which is the layout that
    // was right. Saying so directly makes it immune to board tuning.
    //
    // Capped by the Hold & Win room's background — see
    // HOLD_AND_WIN_BACKGROUND_UNITS.
    if (device === PixiSettings.DEVICE.DESKTOP) {
      // const cap =
      let cap =
        HOLD_AND_WIN_BACKGROUND_UNITS *
        holdAndWinSceneScale(dimensions, layout) *
        // SCENE_BACKGROUND_FILL;
        (SCENE_BACKGROUND_FILL_BY_LAYOUT[layout] ?? SCENE_BACKGROUND_FILL);
      // if (gameViewArtWidth > 0) cap = Math.min(cap, gameViewArtWidth * GAME_VIEW_ART_FILL);
      // The game view's own width decides once it is known (not the H&W room's).
      if (gameViewArtWidth > 0) cap = gameViewArtWidth * GAME_VIEW_ART_FILL;
      return cap > 0 ? Math.min(dimensions.width, Math.round(cap)) : dimensions.width;
    }
    return MAX_TOOLBAR_WIDTH;
  }, [
    device,
    orientation,
    uiSymbolSize,
    dimensions,
    layout,
    isMobileSmall,
    buttonConfig.width,
    gameViewArtWidth,
  ]);

  // Memoize width calculation
  const width = useMemo(
    () => Math.min(dimensions.width, maxWidth),
    [dimensions.width, maxWidth],
  );

  const height = useMemo(() => {
    if (device === PixiSettings.DEVICE.MOBILE && isMobileSmall) {
      return orientation === PixiSettings.ORIENTATION.PORTRAIT
        ? buttonConfig.height * TOOLBAR_HEIGHT_MULTIPLIERS.MOBILE_PORTRAIT
        : buttonConfig.height *
            TOOLBAR_HEIGHT_MULTIPLIERS.MOBILE_SMALL_LANDSCAPE;
    } else if (device === PixiSettings.DEVICE.MOBILE) {
      return orientation === PixiSettings.ORIENTATION.PORTRAIT
        ? buttonConfig.height * TOOLBAR_HEIGHT_MULTIPLIERS.MOBILE_PORTRAIT
        : buttonConfig.height * TOOLBAR_HEIGHT_MULTIPLIERS.MOBILE_LANDSCAPE;
    } else if (device === PixiSettings.DEVICE.TABLET) {
      return orientation === PixiSettings.ORIENTATION.PORTRAIT
        ? buttonConfig.height * TOOLBAR_HEIGHT_MULTIPLIERS.TABLET_PORTRAIT
        : buttonConfig.height * TOOLBAR_HEIGHT_MULTIPLIERS.TABLET_LANDSCAPE;
    } else {
      return buttonConfig.height * TOOLBAR_HEIGHT_MULTIPLIERS.DESKTOP;
    }
  }, [device, orientation, isMobileSmall, buttonConfig.height]);

  // Visual height for the toolbar background sprite (can differ from layout height)
  // In portrait modes, this shows the gradient background without affecting element positioning
  const visualHeight = useMemo(() => {
    if (
      device === PixiSettings.DEVICE.MOBILE &&
      orientation === PixiSettings.ORIENTATION.PORTRAIT
    ) {
      // const multiplier = isMobileSmall
      //   ? TOOLBAR_VISUAL_HEIGHT_MULTIPLIERS.MOBILE_SMALL_PORTRAIT
      //   : TOOLBAR_VISUAL_HEIGHT_MULTIPLIERS.MOBILE_PORTRAIT;
      // An iPad upright has the small phone's places but its own bar.
      const multiplier =
        layout === "TABLET_PORTRAIT"
          ? TOOLBAR_VISUAL_HEIGHT_MULTIPLIERS.TABLET_PORTRAIT
          : isMobileSmall
            ? TOOLBAR_VISUAL_HEIGHT_MULTIPLIERS.MOBILE_SMALL_PORTRAIT
            : TOOLBAR_VISUAL_HEIGHT_MULTIPLIERS.MOBILE_PORTRAIT;
      return buttonConfig.height * multiplier;
    }
    if (
      device === PixiSettings.DEVICE.TABLET &&
      orientation === PixiSettings.ORIENTATION.PORTRAIT
    ) {
      return (
        buttonConfig.height * TOOLBAR_VISUAL_HEIGHT_MULTIPLIERS.TABLET_PORTRAIT
      );
    }
    // Non-portrait: visual height matches layout height
    return height;
  }, [device, orientation, isMobileSmall, layout, buttonConfig.height, height]);

  // Memoize MaxBet button width calculation
  const maxBetWidth = useMemo(() => {
    const maxBetConfig =
      device === PixiSettings.DEVICE.DESKTOP
        ? getResponsiveConfig(MAX_BET_BUTTON_CONFIG_DESKTOP, true)
        : getResponsiveConfig(MAX_BET_BUTTON_CONFIG, true);
    return maxBetConfig.width;
  }, [device, getResponsiveConfig]);

  // Memoize balance width calculation
  const balanceWidth = useMemo(() => {
    const baseFontSize = 32;
    const responsiveFontSize = baseFontSize * fontMultiplier;
    const idealFontSize = Math.round(responsiveFontSize * uiSymbolScale);

    // Calculate max balance width (7-digit balance)
    const maxBalance = 9999999;
    const maxBalanceText = `${formatToCustomLocale(maxBalance)} ${currency}`;
    const tempStyle = new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: idealFontSize,
      fontWeight: "700",
    });
    const maxBalanceMetrics = TextMetrics.measureText(
      maxBalanceText,
      tempStyle,
    );

    return maxBalanceMetrics.width;
  }, [fontMultiplier, uiSymbolScale, currency]);

  // Memoize bet width calculation
  const betWidth = useMemo(() => {
    const baseFontSize = 32;
    const responsiveFontSize = baseFontSize * fontMultiplier;
    const fontSize = Math.round(responsiveFontSize * uiSymbolScale);

    // Calculate max bet width based on largest bet in bets array
    if (!bets || bets.length === 0) {
      return 100; // Fallback width
    }

    const maxBet = Math.max(...bets);
    const maxBetText = `${formatToCustomLocale(maxBet)} ${currency}`;
    const tempStyle = new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: fontSize,
      fontWeight: "700",
    });
    const maxBetMetrics = TextMetrics.measureText(maxBetText, tempStyle);

    return maxBetMetrics.width;
  }, [fontMultiplier, uiSymbolScale, bets, currency]);

  return useMemo(() => {
    // Calculate positions based on device/orientation
    const calculatePositions = () => {
      // Device and orientation specific gap configurations (only for sequential layouts)
      let gap; // Gap between Balance and Bet (or Balance and MaxBet on tablet landscape)
      let maxBetGap; // Gap between Bet and MaxBet

      if (
        device === PixiSettings.DEVICE.MOBILE &&
        orientation === PixiSettings.ORIENTATION.LANDSCAPE
      ) {
        gap = buttonConfig.width * 2.0; // Mobile Landscape gap
        maxBetGap = buttonConfig.width * 2.0; // Mobile Landscape MaxBet gap
      } else if (
        device === PixiSettings.DEVICE.TABLET &&
        orientation === PixiSettings.ORIENTATION.LANDSCAPE
      ) {
        gap = buttonConfig.width * 1.1; // Tablet Landscape gap (Balance to MaxBet)
        maxBetGap = buttonConfig.width * 2.7; // Tablet Landscape MaxBet gap (MaxBet to Bet)
      } else if (device === PixiSettings.DEVICE.DESKTOP) {
        gap = buttonConfig.width * 4.0; // Desktop gap (Balance to Bet)
        maxBetGap = buttonConfig.width * 2.0; // Desktop MaxBet gap (Bet to MaxBet)
      }

      if (device === PixiSettings.DEVICE.MOBILE) {
        if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
          // Portrait: Balance on left, Bet on right (keep separate positioning)
          // MaxBet uses original positioning logic from ButtonMaxBet component
          return {
            balance: {
              x: buttonConfig.width * 0.225,
              width: balanceWidth,
            },
            bet: {
              x: width - buttonConfig.width * 0.225,
              width: betWidth,
            },
          };
        } else {
          // Landscape: Balance -> Bet (left to right)
          // MaxBet uses original positioning logic from ButtonMaxBet component
          const balanceX = buttonConfig.width * 1.5;
          const betX = balanceX + balanceWidth / 2 + gap + betWidth / 2;
          return {
            balance: {
              x: balanceX,
              width: balanceWidth,
            },
            bet: {
              x: betX,
              width: betWidth,
            },
          };
        }
      } else if (device === PixiSettings.DEVICE.TABLET) {
        if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
          // Portrait: Balance on left, Bet on right (keep separate positioning)
          // MaxBet uses original positioning logic from ButtonMaxBet component
          return {
            balance: {
              x: buttonConfig.width * 0.75,
              width: balanceWidth,
            },
            bet: {
              x: width - buttonConfig.width * 0.75,
              width: betWidth,
            },
          };
        } else {
          // Landscape: Balance -> MaxBet -> Bet (left to right)
          // MaxBet goes between Balance and Bet on tablet landscape
          // Account for center-anchored components
          const balanceX = buttonConfig.width * 0.5;
          const maxBetX = balanceX + balanceWidth / 2 + gap + maxBetWidth / 2;
          const betX = maxBetX + maxBetWidth / 2 + gap + betWidth / 2;
          return {
            balance: {
              x: balanceX,
              width: balanceWidth,
            },
            bet: {
              x: betX,
              width: betWidth,
            },
            maxBet: {
              x: maxBetX,
              width: maxBetWidth,
            },
          };
        }
      } else {
        // Desktop: Balance -> Bet -> MaxBet (left to right)
        // Account for center-anchored components
        // Use different gaps: 'gap' between Balance and Bet, 'maxBetGap' between Bet and MaxBet
        const balanceX = buttonConfig.width * 2.5;
        const betX = balanceX + balanceWidth / 2 + gap + betWidth / 2;
        const maxBetX = betX + betWidth / 2 + maxBetGap + maxBetWidth / 2;
        return {
          balance: {
            x: balanceX,
            width: balanceWidth,
          },
          bet: {
            x: betX,
            width: betWidth,
          },
          maxBet: {
            x: maxBetX,
            width: maxBetWidth,
          },
        };
      }
    };

    const layout = calculatePositions();

    return { width, height, visualHeight, layout };
  }, [
    width,
    height,
    visualHeight,
    balanceWidth,
    betWidth,
    maxBetWidth,
    buttonConfig,
    device,
    orientation,
  ]);
};
